import axios, { AxiosInstance, AxiosRequestHeaders } from 'axios';
import CryptoJS from 'crypto-js';
import { setOrgId, setUserId, setUserOrgs } from './auth';
import { getIsLocalDevelopment, getIsNonProdEnv } from '../util-helpers/common';
import { AddInStorage } from '../storage/AddInStorage';
import { nrNoticeError } from '../util-helpers/newrelic';

// Logging function
const log = (type: string, data: any, backgroundColor = 'blue') => {
    if (getIsLocalDevelopment() || getIsNonProdEnv()) {
        console.log(`%c ${type} HTTP:`, `background-color: ${backgroundColor}; color: #ffffff; padding: 20px`, data);
    }
};

// Function to set up interceptors
const setupInterceptors = (instance: any, type: string) => {
    instance.interceptors.request.use(
        (config: any) => {
            log(`${type} Request`, { URL: config.url, payload: config.data }, 'blue');
            return config;
        },
        (error: any) => {
            nrNoticeError(error, {
                type: `${type}RequestError`,
                currentPage: window.location.href
            });
            return Promise.reject(error);
        }
    );

    instance.interceptors.response.use(
        (response: any) => {
            log(`${type} Response`, { Status: response.status, Body: response.data }, 'green');
            return response;
        },
        (error: any) => {
            const errorResponse = error.response || {};
            const status = errorResponse.status;
            const errorData = {
                URL: errorResponse.config?.url,
                Status: status,
                Message: error.message,
                currentPage: window.location.href
            };

            nrNoticeError(error, {
                type: `${type}ResponseError`,
                ...errorData
            });

            return Promise.reject(error);
        }
    );
};

const api = axios.create({});
setupInterceptors(api, 'API');

// ################ OAUTH ########################################
export type EnvironmentType = "LOCAL" | "NONPROD" | "US" | "CA";

interface FVIDSettings {
    STS_AUTHORITY: string;
    CLIENT_ID: string;
    CLIENT_ROOT: string;
    API_BASE: string;
}

interface EnvironmentSettings {
    LOCAL: FVIDSettings;
    NONPROD: FVIDSettings;
    US: FVIDSettings;
    CA: FVIDSettings;
    [key: string]: FVIDSettings;
}

interface User {
    expired: boolean;
    profile: any;
}

export const ENV_SETTNGS: EnvironmentSettings = {
    LOCAL: {
        STS_AUTHORITY: "https://identity-nonprod-public.filevinedev.com",
        CLIENT_ID: "31ec7f87-0922-4ee5-b928-888f73666c37",
        CLIENT_ROOT: "https://localhost:3003",
        API_BASE: "https://fru.filevinedev.com/v2"
    },
    NONPROD: {
        STS_AUTHORITY: "https://identity-nonprod-public.filevinedev.com",
        CLIENT_ID: "31ec7f87-0922-4ee5-b928-888f73666c37",
        CLIENT_ROOT: "https://outlook.filevinedev.com",
        API_BASE: "https://api.filevinedev.com/fv-app/v2"
    },
    US: {
        STS_AUTHORITY: "https://identity.filevine.com",
        CLIENT_ID: "9afe7234-bd2d-421c-9ab7-43bdc413ab84",
        CLIENT_ROOT: "https://outlook.filevine.com",
        API_BASE: "https://api.filevineapp.com/fv-app/v2"
    },
    CA: {
        STS_AUTHORITY: "https://identity.filevine.ca",
        CLIENT_ID: "69715aa3-908d-41d8-87a8-2a240cabde7f",
        CLIENT_ROOT: "https://outlook.filevine.com",
        API_BASE: "https://api.filevineapp.ca/fv-app/v2"
    }
};

export const getEnvironmentSuffix = (): EnvironmentType => {
    const accessToken = AddInStorage.getItem("access_token");
    let environment: EnvironmentType = "LOCAL"; // Default to "LOCAL"

    if (accessToken !== null && window.location.hostname !== "localhost") {
        const tokenParts = accessToken.split('.');
        if (tokenParts.length !== 3) {
            throw new Error('Invalid token format');
        }
        const payload = JSON.parse(atob(tokenParts[1]));
        const issuer = payload.iss;

        environment = issuer === "https://identity.filevine.ca" ? "CA"
            : (issuer === "https://identity.filevine.com" ? "US" : "NONPROD");

        return environment;
    }

    const userDomain = localStorage.getItem('userDomain') || "";
    if (window.location.hostname === "localhost") {
        environment = "LOCAL";
    } else if (window.location.host === "outlook.filevinedev.com") {
        environment = "NONPROD";
    } else if ((!userDomain && window.location.host === "outlook.filevine.com") || userDomain === ".filevineapp.com") {
        environment = "US";
    } else if (userDomain === ".filevineapp.ca") {
        environment = "CA";
    }

    return environment;
};

class FVID {
    static user: User = {
        expired: false,
        profile: undefined
    };

    static authConfig: any = {
        response_type: 'code',
        scope: 'openid profile email phone tenant filevine.v2.api.* theme fv.api.gateway.access fv.auth.tenant.read offline_access',
    }

    static oAuthApi: AxiosInstance;

    static setAuthConfig = () => {
        const environment: EnvironmentType = getEnvironmentSuffix();

        FVID.authConfig.authority = ENV_SETTNGS[environment].STS_AUTHORITY || "";
        FVID.authConfig.client_id = ENV_SETTNGS[environment].CLIENT_ID || "";
        FVID.authConfig.redirect_uri = ENV_SETTNGS[environment].CLIENT_ROOT + "/callback";
        FVID.authConfig.post_logout_redirect_uri = ENV_SETTNGS[environment].CLIENT_ROOT + "/signout";

        FVID.oAuthApi = axios.create({
            baseURL: FVID.authConfig.authority,
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            }
        });
        setupInterceptors(FVID.oAuthApi, 'OAUTH');
    }

    static authorize = async () => {
        const codeVerifier = generateCodeVerifier();
        const codeChallenge = await generateCodeChallenge(codeVerifier);
        localStorage.setItem('code_verifier', codeVerifier);

        const params = {
            client_id: FVID.authConfig.client_id,
            redirect_uri: FVID.authConfig.redirect_uri,
            response_type: FVID.authConfig.response_type,
            scope: FVID.authConfig.scope,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
        };
        const queryString = createQueryString(params);
        window.location.href = `${FVID.authConfig.authority}/connect/authorize?${queryString}`;
    };

    static exchangeCodeForToken = async (code: string) => {
        const codeVerifier = localStorage.getItem('code_verifier');
        if (!codeVerifier) {
            throw new Error('Code verifier not found');
        }
        localStorage.removeItem('code_verifier');

        FVID.setAuthConfig();

        const data = createQueryString({
            grant_type: 'authorization_code',
            client_id: FVID.authConfig.client_id,
            redirect_uri: FVID.authConfig.redirect_uri,
            code,
            code_verifier: codeVerifier,
        });

        const response = await FVID.oAuthApi.post('/connect/token', data);
        AddInStorage.setItem("id_token", response.data.id_token);

        return response.data;
    };

    static refreshToken = async (refreshToken: string) => {
        const data = createQueryString({
            grant_type: 'refresh_token',
            client_id: FVID.authConfig.client_id,
            refresh_token: refreshToken,
        });

        const response = await FVID.oAuthApi.post('/connect/token', data);
        return response.data;
    };

    static getUserInfo = async (accessToken: string) => {
        const response = await FVID.oAuthApi.get('/connect/userinfo', {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        FVID.user = response.data.Body;
        await generateHeaders(true);
        return response.data;
    };

    static logout = async () => {
        try {
            const idToken = AddInStorage.getItem("id_token");
            const refreshToken = AddInStorage.getItem("refresh_token");
            const postLogoutRedirectUri = FVID.authConfig.post_logout_redirect_uri;

            if (idToken) {
                // Create an iframe for silent logout
                const iframe = document.createElement("iframe");
                iframe.style.display = "none"; // Hide iframe

                // Construct logout URL
                const logoutUrl = `${FVID.authConfig.authority}/connect/endsession?id_token_hint=${encodeURIComponent(idToken)}&post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;

                const iframePromise = new Promise((resolve, reject) => {
                    iframe.onload = resolve;
                    iframe.onerror = reject;
                });

                iframe.src = logoutUrl;
                document.body.appendChild(iframe);

                await Promise.race([
                    iframePromise,
                    new Promise((_, reject) => setTimeout(() => reject(new Error("Logout timeout")), 10000))
                ]);

                document.body.removeChild(iframe);
            }

            if (refreshToken) {
                try {
                    await FVID.oAuthApi.post('/connect/revocation', createQueryString({
                        token: refreshToken,
                        token_type_hint: 'refresh_token',
                        client_id: FVID.authConfig.client_id
                    }));
                } catch (error) {
                    console.error("Error revoking refresh token:", error);
                }
            }

            AddInStorage.clear();
            window.location.assign("/");
        } catch (error) {
            console.error("Silent logout failed:", error);
        }
    };

    static getFVIDAccessToken = async () => {
        const accessToken = AddInStorage.getItem("access_token");
        const refreshToken = AddInStorage.getItem("refresh_token");

        if (accessToken) {
            // Decode the access token to extract the expiration time
            const tokenParts = accessToken.split('.');
            if (tokenParts.length !== 3) {
                console.error("Invalid token format.");
                await FVID.logout();
                return null;
            }

            const payload = JSON.parse(atob(tokenParts[1])); // Decode the payload (base64)
            const now = Math.floor(Date.now() / 1000); // current time in seconds

            // Check if the token is expired
            if (now >= payload.exp && refreshToken) {
                try {
                    const newAccessToken = await FVID.refreshToken(refreshToken);

                    AddInStorage.setItem("access_token", newAccessToken.access_token);
                    AddInStorage.setItem("refresh_token", newAccessToken.refresh_token);

                    return newAccessToken.access_token;
                } catch (error) {
                    console.error("Error refreshing token, logging out...", error);
                    await FVID.logout(); // Log the user out on error
                    return null;
                }
            }
        } else {
            await FVID.logout(); // Log the user out if no token is found
            return null;
        }

        return accessToken;
    };
}

FVID.setAuthConfig();

// ################# HELPER FUNCTIONS ################################
export const generateHeaders = async (includeUserIDandOrg = false):
    Promise<AxiosRequestHeaders> => {
    const accessToken = await FVID.getFVIDAccessToken();
    if (!accessToken) {
        return {};
    }

    const headers: AxiosRequestHeaders = {
        Authorization: `Bearer ${accessToken}`,
        "x-fv-clientip": await getClientIP()
    };

    if (includeUserIDandOrg) {
        const orgsAndUserID = await getUserOrgsAndUserID();
        if (orgsAndUserID && orgsAndUserID.orgs
            && orgsAndUserID.orgs.length > 0
            && orgsAndUserID.ID > 0) {
            setUserId(orgsAndUserID.ID);
            setUserOrgs(orgsAndUserID.orgs);
            setOrgId(orgsAndUserID.orgs[0]?.orgId);
            headers["x-fv-orgid"] = orgsAndUserID.orgs[0];
            headers["x-fv-userid"] = orgsAndUserID.ID;
        }
    }
    return headers;
};

const getClientIP = async () => {
    try {
        const request = await api.get(`https://api.ipify.org/?format=json`);
        return request.data.ip;
    } catch (e) {
        console.log(e);
    }
    return "127.0.0.1";
};

const createQueryString = (params: { [key: string]: string }) =>
    Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join('&');

const base64UrlEncode = (str: string) =>
    str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');

// Generate code_verifier
const generateCodeVerifier = () => {
    const codeVerifier = CryptoJS.lib.WordArray.random(32).toString(CryptoJS.enc.Base64);
    return base64UrlEncode(codeVerifier);
};

// Generate code_challenge
const generateCodeChallenge = async (codeVerifier: string) => {
    const hash = CryptoJS.SHA256(codeVerifier);
    return base64UrlEncode(hash.toString(CryptoJS.enc.Base64));
};

export const getUserOrgsAndUserID = async () => {
    const accessToken = await FVID.getFVIDAccessToken();
    if (!accessToken) {
        return null;
    }

    const environment: EnvironmentType = getEnvironmentSuffix();
    const request = await api.post(`${ENV_SETTNGS[environment].API_BASE || ""}/utils/GetUserOrgsWithToken`, null, {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-fv-application': 'outlook-addin'
        }
    });

    return {
        orgs: request.data.orgs, ID: request.data.user.userId.native
    };
};

export default FVID;